import React, { useState } from 'react';
import { ArrowRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RECRUITER_DEMO_URL } from '../../../constants/recruiterContact';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';

const AI_HIRING_BANNER_KEY = 'cevee_hb_ai_banner_dismissed_v2';

/** Slate-toned AI interview nudge — matches RCC sandbox banner. */
export function RccAiNudgeBanner({ className }: { className?: string }) {
  const { t } = useTranslation('hiringBoard');
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AI_HIRING_BANNER_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(AI_HIRING_BANNER_KEY, '1');
    } catch {
      /* noop */
    }
  };

  return (
    <div
      className={cn(
        'relative mb-5 rounded-xl border border-slate-200 bg-slate-50 p-4 pr-10 sm:pr-12',
        className,
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label={t('ai_banner.dismiss')}
        onClick={handleDismiss}
        className="absolute right-1.5 top-1.5 text-slate-400 hover:text-slate-600"
      >
        <X className="size-4" />
      </Button>

      <div className="flex items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
              {t('ai_banner.badge')}
            </span>
            <h3 className="text-sm font-semibold text-slate-900">{t('ai_banner.title')}</h3>
          </div>
          <p className="text-xs leading-relaxed text-slate-600">{t('ai_banner.description')}</p>
        </div>

        <a
          href={RECRUITER_DEMO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1.5 self-center rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"
        >
          {t('ai_banner.cta')}
          <ArrowRight className="size-3.5" />
        </a>
      </div>
    </div>
  );
}
